import React from 'react';
import { useAutoCloseZPrint } from './useAutoCloseZPrint';
import { PosReportView } from './PosReportView';
import sessionCloseClasses from '../settings/session-close.module.scss';

type AutoCloseZPrinterProps = {
  tenantId: string;
  employeeSession: string | null;
  // Increments each time the shell observes a session.closed{autoClosed:true}; carries that id.
  liveAutoClosedSessionId: number | null;
  liveAutoClosedNonce: number;
};

// Invisible shell printer: renders the real Z for auto-closed sessions (live + reconnect catch-up).
const AutoCloseZPrinterComponent: React.FC<AutoCloseZPrinterProps> = ({
  tenantId,
  employeeSession,
  liveAutoClosedSessionId,
  liveAutoClosedNonce,
}) => {
  const { pendingReport, printRef, printSessionZ, flushPendingZPrints } = useAutoCloseZPrint({
    tenantId,
    employeeSession,
  });

  // Reconnect catch-up: print any auto-close Z this device wins, once per employee-session.
  React.useEffect(() => {
    if (!employeeSession) {
      return;
    }
    void flushPendingZPrints();
  }, [employeeSession, flushPendingZPrints]);

  // Live event: shell bumps the nonce on each auto-close so the same id can reprint.
  React.useEffect(() => {
    if (!employeeSession || liveAutoClosedSessionId === null) {
      return;
    }
    void printSessionZ(liveAutoClosedSessionId);
  }, [employeeSession, liveAutoClosedNonce, liveAutoClosedSessionId, printSessionZ]);

  if (!pendingReport) {
    return null;
  }
  return (
    <div className={sessionCloseClasses.offscreen} aria-hidden="true">
      <PosReportView report={pendingReport} variant="print" innerRef={printRef} />
    </div>
  );
};

export const AutoCloseZPrinter = React.memo(AutoCloseZPrinterComponent);
